import { combineReducers, Action, Store } from 'redux';
import courseReducer from './courseSlice';
import programReducer from './programSlice';
import groupReducer from './groupSlice';
import userReducer from './userSlice';
import certificationReducer from './certificationSlice';
import progressReducer from './progressSlice';
import errorReducer from './errorSlice';
import { configureStore } from '@reduxjs/toolkit';
import { Course } from '../model/course';
import { Group } from '../model/group';
import { User } from '../model/user';
import { Program, ProgramUser } from '../model/program';
import { UserCertification } from '../model/certification';
import { UserProgress } from '../model/progress';

interface AppState {
  error: {
    message: string | null
  }
  courses: {
    courses: Course[]
    currentCourse: Course | null
  }
  programs: {
    programs: Program[]
    currentProgram: Program | null
  }
  groups: {
    groups: Group[]
    currentGroup: Group | null
  }
  users: {
    users: User[]
    currentGroupUsers: User[]
    currentProgramUsers: ProgramUser[] | null
    currentClientAdmins: User[]
  }
  certifications: {
    currentUserCertifications: UserCertification[] | null
  }
  progress: {
    currentUsersProgramProgress: UserProgress[] | null
  }
}

// Function to load state from localStorage
function loadState(): AppState | undefined {
  try {
    const serializedState = localStorage.getItem('reduxState');
    if (serializedState === null) {
      return undefined;
    }
    return JSON.parse(serializedState) as AppState;
  } catch (err) {
    return undefined;
  }
}

// Function to save state to localStorage
function saveState(state: AppState): void {
  try {
    if (state === undefined) {
      // Remove the reduxState key from localStorage
      localStorage.removeItem('reduxState');
    } else {
      const serializedState = JSON.stringify(state);
      localStorage.setItem('reduxState', serializedState);
    }
  } catch (err) {
    // Ignore write errors.
  }
}

const persistedState = loadState();

const appReducer = combineReducers({
  error: errorReducer,
  courses: courseReducer,
  programs: programReducer,
  groups: groupReducer,
  users: userReducer,
  certifications: certificationReducer,
  progress: progressReducer,
});

const rootReducer = (state: ReturnType<typeof appReducer> | undefined, action: Action) => {
  if (action.type === 'LOGOUT') {
    state = undefined;
  }
  return appReducer(state, action);
};

const store = configureStore({
  reducer: rootReducer,
  preloadedState: persistedState,
});

store.subscribe(() => {
  saveState(store.getState());
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export default store;